import { Col, Container, ListGroup, Placeholder, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

// Mimics only the LeftPanel (md=3) of ProjectExplorer — the project list is the one piece of UI
// that is always on screen before any project/file is selected, since CenterPanel/RightPanel only
// mount once a project is chosen. Reuses the real "Projects" header title (static, not
// data-dependent), the same ListGroup/Row/Col structure as LeftPanel's real rows, and the real
// `#project-explorer` id/classes (ProjectExplorer.Base.scss gives it height: 100% and
// overflow: hidden) so it fills the viewport exactly like the real page, instead of collapsing to
// its content height. App.tsx also applies the same `project-x-bg forced-scroll` classes to the
// surrounding #main-content container while this is shown, matching ProjectExplorer's own case.
// Shown by App.tsx while `fetchUser` (which returns the whole projectdigest) is in flight.
//
// The ListGroup/ListGroup.Item markup below mirrors ListToolbarView.tsx (the "scroll-list
// flex-grow-1" list) and LeftPanel's real row (`Container.drag-container` > `Row.p-2` >
// `Col.project-title`), so the real rows replace these without any layout/style jump.

const PROJECT_COUNT_CACHE_KEY = "sx-center:lastProjectCount";
const DEFAULT_ROW_COUNT = 8;
const MAX_ROW_COUNT = 20;

// Reads the project count from the previous successful `fetchUser` (cached in userSlice.ts) so the
// skeleton shows roughly the right number of rows for this user instead of an arbitrary guess. Falls
// back to DEFAULT_ROW_COUNT on a first-ever visit, a cleared cache, or when localStorage is
// unavailable. Clamped to MAX_ROW_COUNT so a very large project count can't make the skeleton itself
// slow to render.
function getSkeletonRowCount(): number {
  try {
    const cached = Number(localStorage.getItem(PROJECT_COUNT_CACHE_KEY));
    if (cached > 0) {
      return Math.min(cached, MAX_ROW_COUNT);
    }
  } catch {
    // localStorage unavailable (e.g. private browsing) — fall through to the default.
  }
  return DEFAULT_ROW_COUNT;
}

function SkeletonRow({ width, index }: { width: number; index: number }) {
  return (
    <ListGroup.Item as="li" className="skeleton-row" style={{ animationDelay: `${index * 90}ms` }}>
      <Container fluid className="drag-container p-0">
        <Row className="align-items-center p-2">
          <Col xs={6}>
            <Placeholder as="span" animation="glow" className="project-title mb-1 w-100 d-inline-block">
              <Placeholder xs={width} />
            </Placeholder>
          </Col>
        </Row>
      </Container>
    </ListGroup.Item>
  );
}

export default function ProjectExplorerSkeleton() {
  const { t } = useTranslation();
  const rowCount = getSkeletonRowCount();

  return (
    <Row id="project-explorer" className="forced-scroll gx-1 pt-1 pb-1 ps-2 pe-2">
      <Col md={3} className="pt-2 pb-3 h-100">
        <div id="left-panel" className="d-flex flex-column h-100" style={{ overflow: "hidden" }}>
          <Row className="header-bg-list justify-content-start align-items-center">
            <Col className="ps-4">
              <span className="header-title">{t("projects")}</span>
            </Col>
          </Row>
          <hr />
          <Row className="flex-grow-1" style={{ minHeight: 0 }}>
            <Col className="h-100" style={{ minHeight: 0, padding: 0 }}>
              <ListGroup as="ul" id="project-list" className="scroll-list flex-grow-1">
                {Array.from({ length: rowCount }).map((_, i) => (
                  <SkeletonRow key={i} width={9 - (i % 3)} index={i} />
                ))}
              </ListGroup>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
}
